/**
 * Inventory Unsent PO Exception Config
 *
 * Covers: ex-inv-ls-unsent-po
 *
 * Received purchase orders in Lightspeed without a matching Xero bill.
 */
import { formatCurrency, formatDate } from '../../../utils/inspectorFormatters'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  composeRowSubtitle,
  datePart,
  moneyPart,
  textPart,
} from '../shared/rowSubtitle'
import {
  consignmentReferenceSortValue,
  createConsignmentLinkCell,
} from '../stock-management/shared/consignmentRenderers'
import { createDaysValueRenderer } from '../stock-management/shared/daysRenderers'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'

const renderDaysValue = createDaysValueRenderer({ redAtDays: 30, orangeAtDays: 14 })

const columns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  buildIssuesColumn(),
  {
    key: 'name',
    label: 'Reference',
    render: createConsignmentLinkCell('PO'),
    link: { field: 'lightspeed_link', system: 'lightspeed' },
    getSortValue: consignmentReferenceSortValue,
  },
  {
    key: 'received_at_local',
    label: 'Received Date',
    render: (value, _row, ctx) => ctx.variant === 'inspector' && value ? formatDate(value) : (value || '-'),
    exportFormat: 'date',
  },
  {
    key: 'supplier_name',
    label: 'Supplier',
    render: (value) => value || '-',
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    render: (value) => value || '-',
  },
  {
    key: 'supplier_invoice_number',
    label: 'Supplier Inv. #',
    render: (value) => value || '-',
  },
  {
    key: 'days_value',
    label: 'Days Since Received',
    align: 'right',
    render: renderDaysValue,
    getSortValue: (row) => Number(row.days_value) || 0,
  },
  {
    key: 'total_cost',
    label: 'Total Cost',
    align: 'right',
    render: (value, _row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
]

export const unsentPoConfig: ExceptionConfig = {
  codes: ['ex-inv-ls-unsent-po'],
  columns,
  defaultSortColumn: 'received_at_local',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowTitle: (row) =>
    row.reference_number
      ? `PO ${row.reference_number}`
      : `PO ${String(row.ls_id ?? '').slice(0, 8)}`,
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.supplier_name),
      row.received_at_local ? `received ${datePart(row.received_at_local)}` : null,
      typeof row.days_value === 'number' && row.days_value > 0
        ? `${row.days_value} days unsent`
        : null,
      moneyPart(row.total_cost),
    ]),
}
